import { useEffect, useMemo, useRef, useState } from 'react'
import { Checkbox, Select, Stack, Text, Textarea } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { OnBreakWorkBanner } from '@/components/tickets/OnBreakWorkBanner'
import { AppModal, ActionButton } from '@/components/ui'
import { api, ApiError } from '@/lib/api'
import type { ProjectDetailResponse, ProjectRow, ProjectTemplateRow } from '@/types/api'

type TemplateOption = ProjectTemplateRow & { projectName: string }

type Props = {
  opened: boolean
  onClose: () => void
  ticketIds: string[]
  projects: ProjectRow[]
  onApplied: () => void
}

export function TicketsBulkReplyModal({ opened, onClose, ticketIds, projects, onApplied }: Props) {
  const [prepLoading, setPrepLoading] = useState(false)
  const [requireComment, setRequireComment] = useState(false)
  const [templates, setTemplates] = useState<TemplateOption[]>([])
  const [templateId, setTemplateId] = useState<string | null>(null)
  const [content, setContent] = useState('')
  const [internalNote, setInternalNote] = useState(false)
  const [resolveTickets, setResolveTickets] = useState(false)
  const [closeTickets, setCloseTickets] = useState(false)
  const [resolutionComment, setResolutionComment] = useState('')
  const [dontSendNotifications, setDontSendNotifications] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [frozenTicketIds, setFrozenTicketIds] = useState<string[]>([])
  const openedSessionRef = useRef(false)

  useEffect(() => {
    if (!opened) {
      setSubmitLoading(false)
    }
  }, [opened])

  const templatesById = useMemo(() => {
    const m = new Map<string, TemplateOption>()
    for (const t of templates) m.set(t.id, t)
    return m
  }, [templates])

  useEffect(() => {
    if (!opened) {
      openedSessionRef.current = false
      return
    }
    if (openedSessionRef.current) return
    openedSessionRef.current = true

    const snapshotTicketIds = [...ticketIds]
    const snapshotProjects = [...projects]
    setFrozenTicketIds(snapshotTicketIds)
    if (!snapshotTicketIds.length) return

    let alive = true
    setPrepLoading(true)
    setTemplateId(null)
    setContent('')
    setResolveTickets(false)
    setCloseTickets(false)
    setResolutionComment('')
    setDontSendNotifications(false)
    setInternalNote(false)
    ;(async () => {
      try {
        const detailResults = await Promise.all(
          snapshotProjects.map((p) => api.project(p.id).catch(() => null as ProjectDetailResponse | null)),
        )
        if (!alive) return
        const details = detailResults.filter(Boolean) as ProjectDetailResponse[]
        const needComment = details.some((d) => Boolean(d.require_resolution_comment))
        setRequireComment(needComment)
        const withTpl = details.find((d) => d.resolution_comment_template?.trim())
        const raw = (withTpl?.resolution_comment_template ?? '').replace(/\[resolution_comment\]/g, '')
        if (needComment) setResolutionComment(raw)

        const tplLists = await Promise.all(
          snapshotProjects.map(async (p) => {
            try {
              const list = await api.projectTemplates(p.id)
              return list.map((t) => ({ ...t, projectName: p.name }))
            } catch {
              return [] as TemplateOption[]
            }
          }),
        )
        if (!alive) return
        setTemplates(tplLists.flat())
      } catch {
        if (alive) {
          setRequireComment(false)
          setTemplates([])
        }
      } finally {
        if (alive) setPrepLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [opened, ticketIds, projects])

  useEffect(() => {
    if (!templateId) return
    const t = templatesById.get(templateId)
    if (t) setContent(t.content || '')
  }, [templateId, templatesById])

  const submit = async () => {
    const text = content.trim()
    if (!text) {
      showNotification({ title: 'Текст ответа', message: 'Введите текст ответа.', color: 'yellow' })
      return
    }
    const resComment = resolveTickets && requireComment ? resolutionComment.trim() : ''
    if (resolveTickets && requireComment && !resComment) {
      showNotification({
        title: 'Комментарий к решению',
        message: 'Введите комментарий к решению обращений.',
        color: 'yellow',
      })
      return
    }
    setSubmitLoading(true)
    try {
      await api.ticketsBulkReply({
        ticket_ids: frozenTicketIds,
        content: text,
        is_internal: internalNote,
        resolve_tickets: resolveTickets,
        close_tickets: closeTickets,
        resolution_comment: resComment || undefined,
        send_resolution_notifications: !dontSendNotifications,
      })
      showNotification({
        title: 'Отправлено',
        message: `Ответ ушёл в ${frozenTicketIds.length} обращений.`,
        color: 'green',
      })
      onApplied()
      onClose()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось отправить массовый ответ',
        color: 'red',
      })
    } finally {
      setSubmitLoading(false)
    }
  }

  const templateSelectData = templates.map((t) => ({
    value: t.id,
    label: `${t.projectName} — ${t.name || t.id}`,
  }))

  // Важно: не блокируем модалку на этапе предварительной загрузки шаблонов/настроек.
  // Иначе при долгих API пользователь не может ни закрыть окно, ни ввести ответ.
  const busy = submitLoading

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={`Массовый ответ (${frozenTicketIds.length} обращений)`}
      size="lg"
      footer={
        <>
          <ActionButton action="cancel" onClick={onClose} disabled={busy} />
          <ActionButton
            action="submit"
            label={`Отправить (${frozenTicketIds.length})`}
            onClick={() => void submit()}
            loading={submitLoading}
          />
        </>
      }
    >
      <Stack gap="md">
        <OnBreakWorkBanner />
        {prepLoading ? (
          <Text size="sm" c="dimmed">
            Загрузка шаблонов и настроек проектов…
          </Text>
        ) : null}
        <Select
          label="Шаблон ответа"
          placeholder="Выберите шаблон…"
          data={templateSelectData}
          value={templateId}
          onChange={setTemplateId}
          clearable
          disabled={busy}
        />
        <Text size="xs" c="dimmed">
          Можно использовать команды типа /hello в поле ответа. Переменные [name], [ticket_number], [email]
          подставятся для каждого обращения.
        </Text>
        <Textarea
          label="Текст ответа"
          required
          minRows={6}
          value={content}
          onChange={(e) => setContent(e.currentTarget.value)}
          disabled={busy}
        />
        <Checkbox
          label="Отправить как внутреннюю заметку"
          checked={internalNote}
          onChange={(e) => setInternalNote(e.currentTarget.checked)}
          disabled={busy}
        />
        <Checkbox
          label={
            requireComment
              ? 'Решить обращения после отправки (нужен комментарий)'
              : 'Решить обращения после отправки'
          }
          checked={resolveTickets}
          onChange={(e) => {
            const v = e.currentTarget.checked
            setResolveTickets(v)
            if (v) setCloseTickets(false)
          }}
          disabled={busy}
        />
        <Checkbox
          label="Закрыть обращения после отправки"
          checked={closeTickets}
          onChange={(e) => {
            const v = e.currentTarget.checked
            setCloseTickets(v)
            if (v) setResolveTickets(false)
          }}
          disabled={busy}
        />
        {requireComment && resolveTickets ? (
          <>
            <Textarea
              label="Комментарий к решению"
              required
              minRows={3}
              value={resolutionComment}
              onChange={(e) => setResolutionComment(e.currentTarget.value)}
              disabled={busy}
            />
            <Text size="xs" c="dimmed">
              Отправится пользователям с уведомлением о решении.
            </Text>
            <Checkbox
              label="Не отправлять уведомления пользователям"
              checked={dontSendNotifications}
              onChange={(e) => setDontSendNotifications(e.currentTarget.checked)}
              disabled={busy}
            />
          </>
        ) : null}
      </Stack>
    </AppModal>
  )
}
